/**
 * Financing maths for the on-site estimator.
 *
 * A plain-number port of the app's Unlock sizing, kept here so the marketing
 * site does not pull Prisma into its bundle:
 *
 * - `estimateUnlock`  → `flexUnlockFinancingLimitMinor` / `flexUnlockCashLimitMinor`
 *                       in `modules/flex/domain/portfolio-loan.ts`
 * - `monthlyPayment`  → `pmt` in `services/loans/calculations.ts`
 *
 * Income is left out on purpose. Unlock is sized on the property alone — the
 * back office checks debt burden on the application, where it can ask for
 * evidence — and people estimate their income badly, so a number that moved
 * with it would not be the number the app shows.
 *
 * If a rule changes there, change it here too; `financing.test.ts` pins the
 * expected outputs so drift shows up in CI.
 */

const clampNonNegative = (n: number): number => (n > 0 ? n : 0);

/** Level monthly payment for a principal over a term at an annual rate. */
export const monthlyPayment = ({
  principal,
  annualInterestRate,
  termMonths,
}: {
  principal: number;
  annualInterestRate: number;
  termMonths: number;
}): number => {
  if (principal <= 0 || termMonths <= 0) return 0;
  const r = annualInterestRate / 12;
  if (r === 0) return principal / termMonths;
  return (principal * r) / (1 - Math.pow(1 + r, -termMonths));
};

/** Round down to a display-friendly step, e.g. the nearest 10,000. */
export const floorTo = (value: number, step: number): number =>
  Math.floor(value / step) * step;

/** The app's "Financing up to" and request slider move in these steps. */
export const UNLOCK_STEP = 50_000;

export type UnlockEstimate = {
  /** The most the financing can total: the property value × LTV, capped at the product maximum. */
  financingLimit: number;
  /**
   * Cash in hand once the remaining installments are settled out of that
   * limit, in the app's steps; 0 when it would fall below the minimum.
   */
  cash: number;
  /** Monthly payment on the cash alone, at the term chosen. */
  monthly: number;
  /** Which rule set the cash: the LTV, the product maximum, or the minimum it fell under. */
  limitedBy: "ltv" | "maximum" | "minimum";
};

const limitedBy = (
  financingLimit: number,
  maxAmount: number,
  offered: boolean,
): UnlockEstimate["limitedBy"] => {
  if (!offered) return "minimum";
  return financingLimit === maxAmount ? "maximum" : "ltv";
};

/**
 * Mirrors the app: the financing may total the property value times the LTV
 * cap, no more than the product maximum, whether or not the developer is
 * still owed. What is still owed is settled out of that total and the rest is
 * the cash. Unlock is not offered when the cash would be below the minimum.
 */
export const estimateUnlock = ({
  propertyValue,
  outstandingDues = 0,
  annualInterestRate,
  termMonths,
  maxLoanToValue,
  maxAmount,
  minAmount,
}: {
  propertyValue: number;
  outstandingDues?: number;
  annualInterestRate: number;
  termMonths: number;
  maxLoanToValue: number;
  maxAmount: number;
  minAmount: number;
}): UnlockEstimate => {
  const financingLimit = Math.min(propertyValue * maxLoanToValue, maxAmount);
  const available = clampNonNegative(financingLimit - outstandingDues);
  const offered = available >= minAmount;
  const cash = offered ? floorTo(available, UNLOCK_STEP) : 0;

  return {
    financingLimit,
    cash,
    monthly: monthlyPayment({
      principal: cash,
      annualInterestRate,
      termMonths,
    }),
    limitedBy: limitedBy(financingLimit, maxAmount, offered),
  };
};
